import * as Dialog from '@radix-ui/react-dialog';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { MetricNavGroups } from '@/components/layout/SideNav';
import { useSwipeDismiss } from '@/lib/use-swipe-dismiss';
import type { HoverPrefetcher } from '@/features/metric-studio/prefetch';
import { useMarket } from '@/lib/market/market-context';

/**
 * 手機「更多」：由下往上滑出的玻璃底部面板（取代全高的右側抽屜）。
 *
 *  · 上方是一排「頁面」磁磚（圖示＋名稱＋一句說明），下方是「指標明細」膠囊，一眼看完、不必捲很久。
 *  · 在面板任何地方往下滑（內容在最頂時）超過 90px 或快速下滑就關閉；點背景、按「關閉」、Esc 也可以。
 *  · 高度最多 88dvh，底部讓開 Home 指示條；內容區自己捲動。
 */

interface PageTile {
  to: '/$market/digest' | '/$market/market/sector-rotation' | '/$market/market/action-radar';
  icon: string;
  label: string;
  hint: string;
  badge?: string;
}

const PAGE_TILES: readonly PageTile[] = [
  { to: '/$market/digest', icon: '🗓', label: '盤前摘要', hint: '開盤前的重點整理' },
  { to: '/$market/market/sector-rotation', icon: '🗂', label: '板塊輪動', hint: '資金流向各產業' },
  { to: '/$market/market/action-radar', icon: '⚡', label: '異動雷達', hint: '多因子異動標的', badge: 'NEW' },
];

function Tile({
  to,
  icon,
  label,
  hint,
  badge,
  onNavigate,
}: PageTile & { onNavigate: () => void }) {
  const { market } = useMarket();
  return (
    <Link
      to={to}
      params={{ market }}
      onClick={onNavigate}
      className="glass flex min-h-[84px] flex-col justify-between gap-1 rounded-2xl p-3 text-left transition-transform active:scale-[0.98]"
      activeProps={{ className: 'ring-2 ring-accent/60' }}
    >
      <span className="flex items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-2/70 text-18" aria-hidden="true">
          {icon}
        </span>
        {badge === undefined ? null : (
          <span className="rounded-full bg-accent/20 px-1.5 py-0.5 font-mono text-10 font-bold text-fg-1">
            {badge}
          </span>
        )}
      </span>
      <span>
        <span className="block text-14 font-semibold text-fg-1">{label}</span>
        <span className="block truncate text-11 text-muted">{hint}</span>
      </span>
    </Link>
  );
}

export function MoreSheet({
  open,
  onOpenChange,
  prefetcher,
  extra,
  trigger,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefetcher: HoverPrefetcher;
  /** 額外的頁面磁磚（例如模型健檢，只有閘開著才有）。 */
  extra?: ReactNode;
  trigger?: ReactNode;
}) {
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
  const close = () => {
    onOpenChange(false);
  };
  useSwipeDismiss(panelEl, close, open);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger === undefined ? null : <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="sheet-overlay fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          ref={setPanelEl}
          className="more-sheet glass-strong fixed inset-x-0 bottom-0 z-50 flex max-h-[88dvh] flex-col rounded-t-[28px] border-b-0 text-fg-1 outline-none"
          aria-label="更多"
          onOpenAutoFocus={(event) => {
            // 不要一打開就把焦點（綠色外框）落在「關閉」上。
            event.preventDefault();
          }}
        >
          <div className="select-none px-4 pt-2 pb-1">
            <div className="mx-auto h-1.5 w-10 rounded-full bg-fg-2/30" aria-hidden="true" />
            <div className="flex items-center justify-between pt-3 pb-1">
              <div>
                <Dialog.Title className="text-18 font-bold">更多</Dialog.Title>
                <Dialog.Description className="text-12 text-muted">其餘頁面與各指標明細</Dialog.Description>
              </div>
              <Dialog.Close
                className="glass flex h-10 min-w-10 items-center justify-center rounded-full px-3 text-13 font-medium text-fg-1"
                aria-label="關閉"
              >
                關閉
              </Dialog.Close>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(1rem+var(--safe-bottom))]">
            <nav aria-label="更多頁面">
              <h3 className="px-1 pt-2 pb-2 text-[11px] font-bold tracking-wider text-muted uppercase">頁面</h3>
              <div className="grid grid-cols-2 gap-2.5">
                {PAGE_TILES.map((tile) => (
                  <Tile key={tile.to} {...tile} onNavigate={close} />
                ))}
                {extra}
              </div>

              <h3 className="px-1 pt-5 pb-0 text-[11px] font-bold tracking-wider text-muted uppercase">指標明細</h3>
              <MetricNavGroups prefetcher={prefetcher} onNavigate={close} layout="chips" />
            </nav>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** 模型健檢磁磚（只有模型輸出開放時才有）。 */
export function ModelHealthTile({ onNavigate }: { onNavigate: () => void }) {
  const { market } = useMarket();
  return (
    <Link
      to="/ai/model-health"
      search={{ market }}
      onClick={onNavigate}
      className="glass flex min-h-[84px] flex-col justify-between gap-1 rounded-2xl p-3 text-left transition-transform active:scale-[0.98]"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-2/70 text-18" aria-hidden="true">
        📈
      </span>
      <span>
        <span className="block text-14 font-semibold text-fg-1">模型健檢</span>
        <span className="block truncate text-11 text-muted">模型表現與監控</span>
      </span>
    </Link>
  );
}
